// View 2 — Create Tenant wizard (drawer, 2 steps).
//
// Per design/UI : UX v1.0/for-engineering-tenants/specs/tenants-strings.md.
//
// Step 1: Tenant details (name + slug preview + jurisdiction + region +
//         standing + optional billing details collapsible).
// Step 2: First administrator (optional — fields prefill the future
//         atomic-invite that lands when the tenant-admin portal SPA
//         exists in Phase 1; V1 stores them as stub fields on the tenant
//         doc but does NOT actually invoke AdminCreateUser).
//
// Atomic create: single POST /tenants on the final submit. The backend
// handles the firstAdmin field validation + stores the stub. No
// intermediate state — if the user closes the drawer at step 2 without
// confirming, nothing is created.

import { useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Spinner,
  TextInput,
} from '@gc-coach/design-system';
import type {
  CreateTenantRequest,
  TenantRegion,
  TenantStanding,
} from '@gc-coach/shared';
import { createTenant } from '../api/endpoints.js';
import { useI18n } from '../i18n/index.js';
import { deriveSlug } from './slug.js';
import { flagFromIso } from './flag.js';
import styles from './CreateTenantWizard.module.css';

// V1: only EU-W1 enabled. Other regions shown grayed in the dropdown with
// the "Enterprise tier" inline label (designer decision, open-Q #3).
const REGION_OPTIONS: ReadonlyArray<{
  value: TenantRegion;
  enabled: boolean;
  flag: string;
}> = [
  { value: 'eu-west-1', enabled: true, flag: '🇮🇪' },
  { value: 'us-east-1', enabled: false, flag: '🇺🇸' },
  { value: 'ap-southeast-1', enabled: false, flag: '🇸🇬' },
];

// Curated short-list of common jurisdictions. The backend accepts any
// valid ISO 3166 alpha-2 code; the dropdown caps choice at the obvious
// ones for V1 (Phase 1 may want a full picker).
const JURISDICTION_OPTIONS: ReadonlyArray<string> = [
  'FR',
  'GB',
  'DE',
  'BE',
  'NL',
  'IT',
  'ES',
  'CH',
  'IE',
  'LU',
  'US',
  'CA',
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type WizardStep = 1 | 2;

export interface CreateTenantWizardProps {
  onClose: () => void;
  onCreated: () => void;
}

export function CreateTenantWizard({ onClose, onCreated }: CreateTenantWizardProps) {
  const { t, lang } = useI18n();

  const [step, setStep] = useState<WizardStep>(1);

  // Step 1 fields
  const [name, setName] = useState('');
  const [slugOverride, setSlugOverride] = useState('');
  const [jurisdiction, setJurisdiction] = useState<string>('FR');
  const [region, setRegion] = useState<TenantRegion>('eu-west-1');
  const [standing, setStanding] = useState<TenantStanding>('trial');
  const [showBilling, setShowBilling] = useState(false);
  const [billingEmail, setBillingEmail] = useState('');
  const [planTier, setPlanTier] = useState('');
  const [seatCount, setSeatCount] = useState('');

  // Step 2 fields (all optional)
  const [adminEmail, setAdminEmail] = useState('');
  const [adminFirst, setAdminFirst] = useState('');
  const [adminLast, setAdminLast] = useState('');

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [topError, setTopError] = useState<string | null>(null);

  // Derive the live slug preview (what {slug}.gc-coach.ai will look like)
  const slugPreview = useMemo(
    () =>
      slugOverride.trim().length > 0 ? deriveSlug(slugOverride) : deriveSlug(name),
    [name, slugOverride],
  );

  // ─── Step 1 validation (must pass before allowing Next) ──────────────────

  function validateStep1(): Record<string, string> {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs['name'] = t('tenants.fieldErrorRequired');
    if (!jurisdiction || !/^[A-Z]{2}$/.test(jurisdiction)) {
      errs['jurisdiction'] = t('tenants.fieldErrorRequired');
    }
    if (showBilling && billingEmail.trim() && !EMAIL_RE.test(billingEmail.trim())) {
      errs['billingEmail'] = t('tenants.fieldErrorEmailFormat');
    }
    if (showBilling && seatCount.trim()) {
      const n = Number(seatCount.trim());
      if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
        errs['seatCount'] = t('tenants.fieldErrorInvalidNumber');
      }
    }
    return errs;
  }

  function validateStep2(): Record<string, string> {
    const errs: Record<string, string> = {};
    const anyFilled = adminEmail.trim() || adminFirst.trim() || adminLast.trim();
    if (!anyFilled) return errs; // all optional — skip is allowed
    // If ANY field is filled, all three required
    if (!adminEmail.trim()) errs['firstAdmin.email'] = t('tenants.fieldErrorRequired');
    else if (!EMAIL_RE.test(adminEmail.trim().toLowerCase()))
      errs['firstAdmin.email'] = t('tenants.fieldErrorEmailFormat');
    else if (adminEmail.trim().toLowerCase().endsWith('@gc-coach.ai'))
      errs['firstAdmin.email'] = t('tenants.fieldErrorInternalDomain');
    if (!adminFirst.trim()) errs['firstAdmin.firstName'] = t('tenants.fieldErrorRequired');
    if (!adminLast.trim()) errs['firstAdmin.lastName'] = t('tenants.fieldErrorRequired');
    return errs;
  }

  // ─── Step transitions + submit ───────────────────────────────────────────

  function goToStep2() {
    const errs = validateStep1();
    setFieldErrors(errs);
    if (Object.keys(errs).length === 0) {
      setTopError(null);
      setStep(2);
    }
  }

  async function submit(includeAdmin: boolean) {
    const step2Errs = includeAdmin ? validateStep2() : {};
    setFieldErrors(step2Errs);
    if (Object.keys(step2Errs).length > 0) return;

    setSubmitting(true);
    setTopError(null);

    const body: CreateTenantRequest = {
      name: name.trim(),
      jurisdiction,
      region,
      standing,
      ...(slugOverride.trim() ? { slug: deriveSlug(slugOverride) } : {}),
      ...(showBilling && billingEmail.trim()
        ? { billingEmail: billingEmail.trim().toLowerCase() }
        : {}),
      ...(showBilling && planTier.trim() ? { planTier: planTier.trim() } : {}),
      ...(showBilling && seatCount.trim()
        ? { seatCount: Number(seatCount.trim()) }
        : {}),
      ...(includeAdmin && adminEmail.trim()
        ? {
            firstAdmin: {
              email: adminEmail.trim().toLowerCase(),
              firstName: adminFirst.trim(),
              lastName: adminLast.trim(),
              role: 'tenant_admin' as const,
            },
          }
        : {}),
    };

    const result = await createTenant(body);
    setSubmitting(false);

    if (result.ok) {
      onCreated();
      return;
    }
    if (result.code === 'BAD_REQUEST' && /already exists/i.test(result.message)) {
      setFieldErrors({ name: t('tenants.fieldErrorDuplicateName') });
      setStep(1);
      return;
    }
    if (result.code === 'VALIDATION_FAILED') {
      // Backend may flag slug reserved / invalid jurisdiction / etc.
      // Surface the message at the top + map known field codes.
      setTopError(result.message || t('tenants.createGenericError'));
      // Server's validation `details.fields` is in the message-shape we
      // didn't surface in ApiResult. Top-level alert is enough for V1.
      setStep(1);
      return;
    }
    setTopError(t('tenants.createGenericError'));
  }

  function onSubmitForm(e: FormEvent) {
    e.preventDefault();
    if (step === 1) goToStep2();
  }

  // The hostname for the slug preview uses staging today — env-aware
  // hardcode is fine for V1 (the SPA's env.tenantDomainSuffix doesn't
  // exist yet; add when Phase 1 wires the portal).
  const slugDomain = 'staging.gc-coach.ai';

  return (
    <Drawer
      open
      onClose={submitting ? () => undefined : onClose}
      title={
        step === 1
          ? t('tenants.wizardTitleStep1')
          : t('tenants.wizardTitleStep2')
      }
      footer={
        step === 1 ? (
          <div className={styles.footer}>
            <Button kind="ghost" onClick={onClose} disabled={submitting}>
              {t('tenants.cancel')}
            </Button>
            <Button kind="primary" onClick={goToStep2} disabled={submitting}>
              {t('tenants.wizardNext')}
            </Button>
          </div>
        ) : (
          <div className={styles.footer}>
            <Button kind="ghost" onClick={() => setStep(1)} disabled={submitting}>
              {t('tenants.wizardBack')}
            </Button>
            <Button
              kind="ghost"
              onClick={() => void submit(false)}
              disabled={submitting}
            >
              {submitting ? <Spinner /> : t('tenants.wizardSkipAndCreate')}
            </Button>
            <Button
              kind="primary"
              onClick={() => void submit(true)}
              disabled={submitting}
            >
              {submitting ? <Spinner /> : t('tenants.wizardCreate')}
            </Button>
          </div>
        )
      }
    >
      <form id="create-tenant-form" className={styles.form} onSubmit={onSubmitForm} noValidate>
        {topError && (
          <div className={styles.alert}>
            <Alert severity="danger">{topError}</Alert>
          </div>
        )}

        {step === 1 ? (
          <>
            <TextInput
              label={t('tenants.fieldName')}
              placeholder={t('tenants.fieldNamePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              errorMessage={fieldErrors['name']}
              autoComplete="off"
              required
            />

            <div>
              <TextInput
                label={t('tenants.fieldSlug')}
                placeholder={deriveSlug(name) || t('tenants.fieldSlugPlaceholder')}
                value={slugOverride}
                onChange={(e) => setSlugOverride(e.target.value)}
                errorMessage={fieldErrors['slug']}
                hint={t('tenants.fieldSlugHint')}
                autoComplete="off"
              />
              {slugPreview && (
                <div className={styles.slugPreview}>
                  {slugPreview}.{slugDomain}
                </div>
              )}
            </div>

            <div className={styles.jurisdictionSelectWrap}>
              <span className={styles.jurisdictionLabel}>{t('tenants.fieldJurisdiction')}</span>
              <select
                className={styles.nativeSelect}
                value={jurisdiction}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setJurisdiction(e.target.value)}
              >
                {JURISDICTION_OPTIONS.map((j) => (
                  <option key={j} value={j}>
                    {flagFromIso(j)} {j}
                  </option>
                ))}
              </select>
              {fieldErrors['jurisdiction'] && (
                <span className={styles.errorText}>{fieldErrors['jurisdiction']}</span>
              )}
            </div>

            <div className={styles.regionSelectWrap}>
              <span className={styles.regionLabel}>{t('tenants.fieldRegion')}</span>
              <select
                className={styles.nativeSelect}
                value={region}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  setRegion(e.target.value as TenantRegion)
                }
              >
                {REGION_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value} disabled={!r.enabled}>
                    {r.flag} {regionLabel(r.value, t)}
                    {!r.enabled ? ` — ${t('tenants.regionEnterpriseTier')}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <fieldset>
              <legend className={styles.legend}>{t('tenants.fieldStanding')}</legend>
              <div className={styles.standingRadioGroup}>
                <StandingRadio
                  value="trial"
                  checked={standing === 'trial'}
                  label={t('tenants.standingTrialLabel')}
                  desc={t('tenants.standingTrialDesc')}
                  onChange={() => setStanding('trial')}
                />
                <StandingRadio
                  value="active"
                  checked={standing === 'active'}
                  label={t('tenants.standingActiveLabel')}
                  desc={t('tenants.standingActiveDesc')}
                  onChange={() => setStanding('active')}
                />
              </div>
            </fieldset>

            <details
              className={styles.collapsibleDetails}
              open={showBilling}
              onToggle={(e) => setShowBilling((e.target as HTMLDetailsElement).open)}
            >
              <summary className={styles.collapsibleSummary}>
                {t('tenants.addBillingDetails')}
              </summary>
              <div className={styles.form} style={{ marginTop: 12, gap: 12 }}>
                <TextInput
                  label={t('tenants.fieldBillingEmail')}
                  type="email"
                  value={billingEmail}
                  onChange={(e) => setBillingEmail(e.target.value)}
                  errorMessage={fieldErrors['billingEmail']}
                  autoComplete="off"
                />
                <div className={styles.row2}>
                  <TextInput
                    label={t('tenants.fieldPlanTier')}
                    value={planTier}
                    onChange={(e) => setPlanTier(e.target.value)}
                    autoComplete="off"
                  />
                  <TextInput
                    label={t('tenants.fieldSeatCount')}
                    type="number"
                    value={seatCount}
                    onChange={(e) => setSeatCount(e.target.value)}
                    errorMessage={fieldErrors['seatCount']}
                    autoComplete="off"
                  />
                </div>
              </div>
            </details>
          </>
        ) : (
          <>
            <div className={styles.stepBanner}>
              <div className={styles.stepTitle}>{t('tenants.wizardDeferralTitle')}</div>
              {t('tenants.wizardDeferralBody')}
            </div>

            <TextInput
              label={t('tenants.fieldAdminEmail')}
              type="email"
              hint={t('tenants.fieldAdminEmailHint')}
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              errorMessage={fieldErrors['firstAdmin.email']}
              autoComplete="off"
            />
            <div className={styles.row2}>
              <TextInput
                label={t('tenants.fieldAdminFirstName')}
                value={adminFirst}
                onChange={(e) => setAdminFirst(e.target.value)}
                errorMessage={fieldErrors['firstAdmin.firstName']}
                autoComplete="off"
              />
              <TextInput
                label={t('tenants.fieldAdminLastName')}
                value={adminLast}
                onChange={(e) => setAdminLast(e.target.value)}
                errorMessage={fieldErrors['firstAdmin.lastName']}
                autoComplete="off"
              />
            </div>
            <div>
              <span className={styles.legend}>{t('tenants.fieldAdminRole')}</span>
              <div style={{ marginTop: 6 }}>
                <span className={styles.tenantAdminChip}>
                  {t('tenants.adminRoleChipLabel')}
                </span>
              </div>
            </div>
          </>
        )}
        {/* Locale toggle source: SPA i18n. Persisted nowhere (V1 wizard
            doesn't send locale on create — backend uses inviter's locale
            via the standard Cognito flow once Phase 1 wires the invite). */}
        {lang === 'EN' || lang === 'FR' ? null : null}
      </form>
    </Drawer>
  );
}

// ─── Standing radio ──────────────────────────────────────────────────────────

function StandingRadio({
  value,
  checked,
  label,
  desc,
  onChange,
}: {
  value: TenantStanding;
  checked: boolean;
  label: string;
  desc: string;
  onChange: () => void;
}) {
  return (
    <label
      className={[
        styles.standingRadioOption,
        checked ? styles.standingRadioActive : null,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <input
        type="radio"
        name="standing"
        value={value}
        checked={checked}
        onChange={onChange}
      />
      <span className={styles.standingRadioBody}>
        <span className={styles.standingRadioLabel}>{label}</span>
        <span className={styles.standingRadioDesc}>{desc}</span>
      </span>
    </label>
  );
}

function regionLabel(r: TenantRegion, t: (k: string) => string): string {
  if (r === 'eu-west-1') return t('tenants.regionEuW1');
  if (r === 'us-east-1') return t('tenants.regionUsE1');
  return t('tenants.regionApSe1');
}
